import { fetchWrapper, IBaseResponse } from './apiBase';
import { BASE_API_URL } from '@/config/environment';
import { IRemoteWorkspace } from './workspaces/workspaceApi';
import { IRepositoryCommitResponse } from '@/lib/repositoryCommitsApi';
import { IRepository } from '@/types';

export interface IMarimoSandboxResponse extends IBaseResponse {
  notebook: IMarimoSandbox;
}

export interface IMarimoSandboxesResponse extends IBaseResponse {
  notebooks: IMarimoSandbox[];
}

export interface IMarimoSandbox {
  id: string;
  status: string;
  tunnel_url: string;
  modal_sandbox_id: string;
  filepath: string;
  path?: string;
  cpu_cores: number;
  memory_mb: number;
  started_at: string;
  finished_at: string | null;
  runtime_seconds: number;
  timeout_secs: number;
  compute_rate: number;
  memory_rate: number;
  credits_used: number;
  created_by_id: string;
  repository_id: string;
  workspace_id: string;
  workspace: IRemoteWorkspace;
  inserted_at: string;
  updated_at: string;
}

export interface IMarimoSandboxConfig {
  gpu_model?: string;
  cpu_cores: number;
  memory_mb: number;
  timeout_secs: number;
  notebook_image_id?: string;
  notebook_base_image_id?: string;
  build_script?: string;
}

export async function createMarimoSandbox(
  namespace: string,
  repoName: string,
  resource: string,
  token?: string,
  config?: IMarimoSandboxConfig,
): Promise<IMarimoSandboxResponse> {
  const targetUrl = `${BASE_API_URL}/repos/${namespace}/${repoName}/notebooks/file/${resource}`;

  const response = await fetchWrapper({
    resourceName: 'marimoSandbox',
    targetUrl,
    method: 'POST',
    contentType: 'application/json',
    authToken: token,
    resourceContext: { namespace, repoName, resource },
    body: JSON.stringify(config),
  });

  return response;
}

export async function createMarimoFileTemplate({
  repository,
  branch,
  resource,
  commitMessage,
  token,
}: {
  repository: IRepository;
  branch: string;
  resource: string;
  commitMessage: string;
  token?: string;
}): Promise<IRepositoryCommitResponse> {
  const { namespace, name: repoName } = repository;
  const targetUrl = `${BASE_API_URL}/repos/${namespace}/${repoName}/notebooks/create/${branch}/${resource}`;

  const response = await fetchWrapper({
    resourceName: 'marimoSandbox',
    targetUrl,
    method: 'POST',
    contentType: 'application/json',
    authToken: token,
    resourceContext: { namespace, repoName, resource },
    body: JSON.stringify({ commit_message: commitMessage }),
  });

  return response;
}

export async function getMarimoSandboxes(
  namespace: string,
  repoName: string,
  token?: string,
): Promise<IMarimoSandboxesResponse> {
  const targetUrl = `${BASE_API_URL}/repos/${namespace}/${repoName}/notebooks`;

  return await fetchWrapper({
    resourceName: 'marimoSandbox',
    targetUrl,
    authToken: token,
  });
}

export async function runMarimoSandbox(
  namespace: string,
  repoName: string,
  sandboxId: string,
  token?: string,
): Promise<IMarimoSandboxResponse> {
  const targetUrl = `${BASE_API_URL}/repos/${namespace}/${repoName}/notebooks/${sandboxId}`;

  return await fetchWrapper({
    resourceName: 'marimoSandbox',
    targetUrl,
    method: 'POST',
    authToken: token,
  });
}

export async function deleteMarimoSandbox(
  namespace: string,
  repoName: string,
  sandboxId: string,
  token?: string,
): Promise<IMarimoSandboxResponse> {
  const targetUrl = `${BASE_API_URL}/repos/${namespace}/${repoName}/notebooks/${sandboxId}`;

  return await fetchWrapper({
    resourceName: 'marimoSandbox',
    targetUrl,
    method: 'DELETE',
    authToken: token,
    resourceContext: { namespace, repoName, sandboxId },
  });
}

export async function getMarimoSandbox(
  namespace: string,
  repoName: string,
  sandboxId: string,
  token?: string,
): Promise<IMarimoSandboxResponse> {
  const targetUrl = `${BASE_API_URL}/repos/${namespace}/${repoName}/notebooks/${sandboxId}`;

  return await fetchWrapper({
    resourceName: 'marimoSandbox',
    targetUrl,
    authToken: token,
  });
}
